import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { fetchAuthSession } from "aws-amplify/auth"
import { Hub } from "@aws-amplify/core"
import * as auth from "../../utils/auth-functions/auth-functions"
import {
  extractUserFromToken,
  isTokenExpiringSoon,
} from "../../utils/session/session.utils"
import type {
  AuthContextValue,
  AuthProviderProps,
  AuthState,
} from "./AuthProvider.types"

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
  children,
  config,
}) => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    isError: false,
    error: null,
    user: null,
  })

  // Configuration with defaults
  const {
    tokenRefreshInterval = 240000, // 4 minutes default
    enableTokenRefresh = true,
    enableDebugLogging = false,
  } = config || {}

  const log = useCallback(
    (...args: unknown[]) => {
      if (enableDebugLogging) {
        console.log("[AuthProvider]", ...args)
      }
    },
    [enableDebugLogging]
  )

  // Check for existing Cognito session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        log("Checking for existing session...")
        // Force refresh to validate the refresh token with Cognito.
        // This ensures revoked sessions (via AdminUserGlobalSignOut) are
        // detected immediately on page load rather than waiting for the
        // cached access token to expire.
        const { tokens } = await fetchAuthSession({ forceRefresh: true })
        const idToken = tokens?.idToken
        const accessToken = tokens?.accessToken

        if (idToken?.payload?.sub && accessToken) {
          const user = await extractUserFromToken(idToken)
          log("Session found, user authenticated:", user.email)

          setAuthState({
            isLoading: false,
            isAuthenticated: true,
            isError: false,
            error: null,
            user,
          })
        } else {
          // No session, user needs to log in
          log("No session found, user needs to log in")
          setAuthState({
            isLoading: false,
            isAuthenticated: false,
            isError: false,
            error: null,
            user: null,
          })
        }
      } catch (err) {
        console.error("Session check error:", err)
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          isError: false,
          error: null,
          user: null,
        })
      }
    }

    checkSession()
  }, [log])

  // Listen for OAuth completion and other auth events via Hub
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      log("Auth event received:", payload.event)

      if (payload.event === "signInWithRedirect") {
        // OAuth completed - fetch the new session
        try {
          log("OAuth sign-in completed, fetching session...")
          const { tokens } = await fetchAuthSession({ forceRefresh: true })
          const idToken = tokens?.idToken
          const accessToken = tokens?.accessToken

          if (idToken?.payload?.sub && accessToken) {
            const user = await extractUserFromToken(idToken)
            log("OAuth authentication successful:", user.email)

            setAuthState({
              isLoading: false,
              isAuthenticated: true,
              isError: false,
              error: null,
              user,
            })
          }
        } catch (err) {
          console.error("OAuth callback error:", err)
          setAuthState({
            isLoading: false,
            isAuthenticated: false,
            isError: true,
            error: err instanceof Error ? err : new Error("OAuth failed"),
            user: null,
          })
        }
      } else if (payload.event === "signInWithRedirect_failure") {
        console.error("OAuth redirect failed:", payload.data?.error)
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          isError: true,
          error: payload.data?.error || new Error("OAuth sign-in failed"),
          user: null,
        })
      } else if (payload.event === "signedOut") {
        log("User signed out")
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          isError: false,
          error: null,
          user: null,
        })
      } else if (payload.event === "signedIn") {
        // Handle direct sign-in (email/password)
        try {
          log("Direct sign-in completed, fetching session...")
          const { tokens } = await fetchAuthSession({ forceRefresh: true })
          const idToken = tokens?.idToken
          const accessToken = tokens?.accessToken

          if (idToken?.payload?.sub && accessToken) {
            const user = await extractUserFromToken(idToken)
            log("Authentication successful:", user.email)

            setAuthState({
              isLoading: false,
              isAuthenticated: true,
              isError: false,
              error: null,
              user,
            })
          }
        } catch (err) {
          console.error("Sign-in callback error:", err)
          setAuthState({
            isLoading: false,
            isAuthenticated: false,
            isError: true,
            error: err instanceof Error ? err : new Error("Sign-in failed"),
            user: null,
          })
        }
      }
    })

    return unsubscribe
  }, [log])

  const handleLogout = useCallback(
    async (options?: { onLogoutComplete?: () => void }) => {
      log("Logging out...")
      await auth.logout()
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        isError: false,
        error: null,
        user: null,
      })
      if (options?.onLogoutComplete) {
        options.onLogoutComplete()
      }
    },
    [log]
  )

  const handleGetAccessTokenSilently = useCallback(async () => {
    const accessToken = await auth.getAccessToken()
    if (!accessToken) {
      throw new Error("Unable to retrieve access token from Cognito session")
    }
    return accessToken
  }, [])

  const handleLoginWithRedirect = useCallback(
    async (options?: { provider?: string }) => {
      log("Initiating login redirect...", options)
      await auth.loginWithRedirect(options)
    },
    [log]
  )

  // Monitor token expiration and refresh proactively
  useEffect(() => {
    if (!authState.isAuthenticated || !enableTokenRefresh) return

    const checkTokenExpiration = async () => {
      try {
        const { tokens } = await fetchAuthSession()
        const accessToken = tokens?.accessToken

        if (accessToken?.payload?.exp) {
          const expirationTime = accessToken.payload.exp as number

          if (isTokenExpiringSoon(expirationTime)) {
            log("Token expiring soon, refreshing...")
            // Proactively refresh the session
            await fetchAuthSession({ forceRefresh: true })
            log("Token refreshed successfully")
          }
        }
      } catch (err) {
        console.error("Token refresh check error:", err)
      }
    }

    // Check token expiration at configured interval
    const interval = setInterval(checkTokenExpiration, tokenRefreshInterval)

    // Run initial check
    checkTokenExpiration()

    return () => clearInterval(interval)
  }, [authState.isAuthenticated, enableTokenRefresh, tokenRefreshInterval, log])

  const contextValue: AuthContextValue = useMemo(
    () => ({
      ...authState,
      logout: handleLogout,
      loginWithRedirect: handleLoginWithRedirect,
      getAccessTokenSilently: handleGetAccessTokenSilently,
    }),
    [
      authState,
      handleLogout,
      handleLoginWithRedirect,
      handleGetAccessTokenSilently,
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export { AuthContext }
